import { V2_TIER_BY_TRIAL_PLAN, V2_TRIAL_ENDING_LEAD_DAYS, isV2TrialPlan } from '@highlight/common'
import type { BillingPlanV2TrialTier, PaywallState, SubscriptionInfo } from '@highlight/common'
import { isLegacyPricingPlan, v2TierFor } from '@renderer/chat/utils/billingUtils'
import type { TopUpKind } from '@renderer/components/pricing/top-up-kinds'
import { isOutOfMeetingHours } from '../meeting/use-out-of-meeting-hours'
import type { SidepanelPaywallRequest } from './sidepanel-paywall-request'

// Pure module, no atoms or React, so the strip rules stay testable.

const DAY_MS = 24 * 60 * 60 * 1000

export interface TopUpStrip {
  kind: TopUpKind
  /** Whether the allowance this strip refills is spent, which picks its copy. */
  atLimit: boolean
}

export interface TopUpStripState {
  /** Credits before meeting hours, so the order holds as strips come and go. */
  strips: Array<TopUpStrip>
  /** A request the meters have since contradicted; the caller drops it. */
  staleRequest: TopUpKind | null
}

const KINDS: Array<TopUpKind> = ['credits', 'hours']

/**
 * Whether sidepanel home sells a plan to this account at all: it holds no v2
 * tier and so can buy no top-up. Free, and the lapsed accounts that flip to it,
 * qualify; the strips then decide when the pitch shows.
 *
 * A legacy pricing plan maps to no tier either but is paid, and the backend
 * enforces neither credits nor meeting hours on it (see isLegacyPricingPlan),
 * so a pitch over it is a standing nag over a plan that works. Over eat it
 * also lies: the server presents eat as 'pro-trial', its meeting allowance is
 * unlimited, and the strip reads "Upgrade to Pro to transcribe your meetings"
 * (HIG-6548). Switching to a v2 tier stays available in Settings.
 */
export function sellsPlan(paywall: PaywallState): boolean {
  return !paywall.isLoading && v2TierFor(paywall.plan) === null && !isLegacyPricingPlan(paywall.plan)
}

/**
 * What each meter says about its allowance: spent, headroom, or null when the
 * plan grants none, which is no verdict at all.
 *
 * A tier's meters always rule, 0-of-0 reading as spent, since a tier can buy
 * more. A plan with no tier is read by what it was granted: a free plan that
 * holds no credits and no hours has nothing to run out of, so no meter fires
 * and no refresh can contradict a refusal it got. Once free grants an
 * allowance, its meters rule like a tier's. Both of free's verdicts come from
 * the meter numbers, not `paywall.atLimit`: the main process pins that flag
 * off for free, so it can only ever read a granted balance as headroom.
 */
function meterVerdicts(paywall: PaywallState, subscription: SubscriptionInfo): Record<TopUpKind, boolean | null> {
  if (v2TierFor(paywall.plan)) {
    return { credits: paywall.atLimit, hours: isOutOfMeetingHours(paywall, subscription) }
  }
  const { credits, meetingHours } = paywall
  return {
    credits: credits.total > 0 ? credits.percentage >= 100 || credits.remaining <= 0 : null,
    hours: meetingHours.total !== null && meetingHours.total > 0 ? meetingHours.percentage >= 100 : null,
  }
}

/**
 * Which top-up strips the sidepanel home docks, and whether each one is selling
 * against a spent allowance or offering headroom. A plan with no tier docks the
 * plan promo instead, on the same strips (see sellsPlan).
 *
 * A request docks a strip the meters don't ask for yet, covering the window
 * where a refusal lands before the billing refresh does. Requests have to
 * expire. Nothing can dismiss the docked strip, so one that outlives its reason
 * pins "you've used all your hours" over a meter reading 0% (HIG-6471). A
 * refresh that lands after the request and still reads headroom retires it.
 */
export function resolveTopUpStrips(params: {
  paywall: PaywallState
  subscription: SubscriptionInfo
  request: SidepanelPaywallRequest | null
  /** UnifiedBillingState.lastUpdated, when this paywall state was computed. */
  billingUpdatedAt: number
}): TopUpStripState {
  const { paywall, subscription, request, billingUpdatedAt } = params
  const verdict = meterVerdicts(paywall, subscription)

  // A meter that hasn't loaded is not a verdict, so it retires nothing.
  const requestedKind = request?.kind ?? null
  const staleRequest =
    request && !paywall.isLoading && billingUpdatedAt > request.requestedAt && verdict[request.kind] === false
      ? request.kind
      : null

  return {
    strips: KINDS.filter((kind) => verdict[kind] || (requestedKind === kind && staleRequest !== kind)).map((kind) => ({
      kind,
      atLimit: verdict[kind] === true,
    })),
    staleRequest,
  }
}

export interface TrialEndingStrip {
  tier: BillingPlanV2TrialTier
  /** Whole days until the trial ends, rounded up; never below one. */
  daysLeft: number
}

/**
 * Whether sidepanel home docks the trial-ending paywall. A v2 trial started
 * without a card ends on the free plan, so inside the lead window the
 * trial-ending email also uses, it sells the tier and asks for the card. A
 * card-holder's trial converts on its own, or they cancelled it, so it gets no
 * strip. Once the plan flips to free the plan promo takes over.
 */
export function docksTrialEnding(
  paywall: PaywallState,
  subscription: SubscriptionInfo,
  now = Date.now(),
): TrialEndingStrip | null {
  if (paywall.isLoading || !isV2TrialPlan(paywall.plan) || !paywall.trialEndsAt) {
    return null
  }
  if (subscription.hasPaymentMethod === true) {
    return null
  }
  // Past the end only Orb's apply is outstanding. A card saved now could keep no days, so
  // nothing is sold until the plan flips and the promo takes over.
  const msLeft = new Date(paywall.trialEndsAt).getTime() - now
  if (msLeft <= 0) {
    return null
  }
  if (Math.ceil(msLeft / DAY_MS) > V2_TRIAL_ENDING_LEAD_DAYS) {
    return null
  }
  // Whole days left, so the last 24 hours read as ending today rather than tomorrow.
  return { tier: V2_TIER_BY_TRIAL_PLAN[paywall.plan], daysLeft: Math.floor(msLeft / DAY_MS) }
}
